import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { PublicPack } from '@/types';
import { api } from '@/client/lib/api';
import { useMeta } from '@/client/lib/useMeta';
import { useCart, availableForSize, itemKey, packLineKey } from '@/features/cart/CartContext';
import { useUI } from '@/client/ui/UIContext';
import { useI18n } from '@/client/i18n/I18nContext';
import { track } from '@/client/lib/analytics';
import { Price } from '@/components/Price';
import { FramedImage } from '@/components/FramedImage';
import { PackCollage } from '@/components/PackCollage';
import { Breadcrumb } from '@/components/Breadcrumb';
import { LoadingState, ErrorState } from '@/components/States';
import { PACK_PATH, maxPacksForSizes, packDescription, packName } from '@/lib/catalog/packs';
import { packPageText } from '@/lib/seo';
import { localizeProduct } from '@/lib/i18n-content';
import { productSizes } from '@/lib/cart';
import { formatMoney } from '@/lib/money';
import { lp } from '@/client/i18n/locale';
import { useLogicalParams } from '@/client/i18n/useLogicalParams';

/**
 * A pack page (`/shop/pack/:slug`, docs/plan-packs.md): photo or collage, name,
 * price now / before and the saving (all from the API: the browser never
 * computes a pack price), the products it includes linked to their pages, one
 * size per sized product, quantity and «Añadir al carrito». A pack not
 * available now (a component short) still answers, noindex, with a notice and
 * the product links but no button; a missing/draft/archived one is a 404.
 */
export function PackPage() {
  const { slug } = useLogicalParams<'slug'>(`${PACK_PATH}/:slug`);
  const [pack, setPack] = useState<PublicPack | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  // Chosen size per sized component (productId → size).
  const [sizes, setSizes] = useState<Record<string, string>>({});
  const { addPack, items } = useCart();
  const { openCart } = useUI();
  const { t, locale } = useI18n();

  // Same title/description/robots as the Worker (lib/seo packPageText); a pack
  // not available now is noindex.
  useMeta({
    ...(pack ? packPageText(pack, locale) : { title: t('pack.metaTitle') }),
    path: `${PACK_PATH}/${slug}`,
    type: 'product',
    image: pack?.image ?? undefined,
    noindex: pack ? !pack.available : undefined,
  });

  useEffect(() => {
    if (!slug) return;
    let alive = true;
    setPack(null);
    setNotFound(false);
    setSizes({});
    setQty(1);
    api
      .pack(slug)
      .then((p) => {
        if (!alive) return;
        setPack(p);
        track('view_product', { id: `pack:${p.id}`, name: p.name ?? p.slug });
      })
      .catch(() => alive && setNotFound(true));
    return () => {
      alive = false;
    };
  }, [slug]);

  // Components in pack order, localized for display (and for the cart line).
  const view = useMemo<PublicPack | null>(
    () => (pack ? { ...pack, components: pack.components.map((c) => localizeProduct(c, locale)) } : null),
    [pack, locale],
  );
  const lines = useMemo(
    () =>
      view
        ? view.items.flatMap((it) => {
            const product = view.components.find((c) => c.id === it.productId);
            return product ? [{ product, quantity: it.quantity, sizes: productSizes(product) }] : [];
          })
        : [],
    [view],
  );

  // Cap: whole packs the stock allows with the chosen sizes, minus this line in the cart.
  const cap = view ? maxPacksForSizes(view, view.components, sizes) : 0;
  const inCart = view ? (items.find((i) => itemKey(i) === packLineKey(view.id, sizes))?.quantity ?? 0) : 0;
  const maxAddable = Math.max(0, cap - inCart);

  useEffect(() => {
    setQty((q) => Math.min(Math.max(1, q), Math.max(1, maxAddable)));
  }, [maxAddable]);

  if (notFound) {
    return <ErrorState title={t('pack.notFoundTitle')} message={t('pack.notFoundMsg')} />;
  }
  if (!view) return <LoadingState />;

  const name = packName(view, locale);
  const description = packDescription(view, locale);
  const currency = view.components[0]?.currency ?? 'EUR';
  const saving = view.compareAtCents - view.priceCents;
  const missingSize = lines.some((l) => l.sizes.length > 0 && !sizes[l.product.id]);
  const available = view.available;

  return (
    <div className="container">
      <Breadcrumb
        items={[
          { name: t('common.home'), path: '/' },
          { name: t('nav.shop'), path: '/shop' },
          { name: t(`nav.${view.primaryCategory}`), path: `/shop/${view.primaryCategory}` },
          { name },
        ]}
      />

      <div className="pdp" style={{ paddingBottom: 'var(--space-8)' }}>
        <div className="pdp__gallery">
          {view.image ? (
            <FramedImage src={view.image} alt={name} fallback="CARO" />
          ) : (
            <PackCollage items={lines.map((l) => l.product)} alt={name} sizes="(min-width: 900px) 27vw, 50vw" />
          )}
        </div>

        <div className="pdp__info stack">
          <p className="eyebrow">{t('pack.badge').replace('{pct}', String(view.discountPercent))}</p>
          <h1>{name}</h1>

          <div style={{ fontSize: 'var(--fs-xl)' }}>
            <Price cents={view.priceCents} currency={currency} compareAtCents={view.compareAtCents} />
          </div>
          {saving > 0 ? (
            <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
              {t('pack.savings').replace('{amount}', formatMoney(saving, currency))}
            </p>
          ) : null}

          {description ? <p className="lede">{description}</p> : null}

          {!available ? (
            <p className="sold-note" role="status">
              <span className="sold-note__tag">{t('pack.unavailable')}</span>
              <span className="sold-note__text">{t('pack.unavailableHint')}</span>
            </p>
          ) : null}

          <section aria-labelledby="pack-contents" style={{ marginTop: 'var(--space-3)' }}>
            <h2 id="pack-contents" className="eyebrow" style={{ marginBottom: 'var(--space-2)' }}>
              {t('pack.contents')}
            </h2>
            <ul className="pack-items">
              {lines.map(({ product, quantity, sizes: options }) => (
                <li key={product.id} className="pack-items__item">
                  <span>
                    <span className="pack-items__qty">{quantity}×</span>{' '}
                    <Link to={lp(`/shop/${product.category}/${product.slug}`)} className="link-underline">
                      {product.name}
                    </Link>
                    {product.brand ? <span className="text-muted"> · {product.brand}</span> : null}
                    {product.metadata.preorder === true ? (
                      <span className="card__preorder" style={{ display: 'block', marginTop: 'var(--space-1)' }}>
                        {t('product.preorderBadge')}
                      </span>
                    ) : null}
                  </span>
                  {available && options.length > 0 ? (
                    <fieldset className="size-picker">
                      <legend className="size-picker__legend">
                        {t('pack.sizeFor').replace('{name}', product.name)}
                      </legend>
                      <div className="size-picker__options">
                        {options.map((s) => {
                          // A size must cover the units of ONE pack (pre-orders never run out).
                          const out =
                            product.metadata.preorder !== true && availableForSize(product, s) < quantity;
                          const selected = sizes[product.id] === s;
                          return (
                            <label
                              key={s}
                              className={`size-picker__option${selected ? ' is-selected' : ''}${out ? ' is-disabled' : ''}`}
                            >
                              <input
                                type="radio"
                                className="size-picker__input"
                                name={`pack-size-${product.id}`}
                                value={s}
                                checked={selected}
                                disabled={out}
                                onChange={() => setSizes((prev) => ({ ...prev, [product.id]: s }))}
                              />
                              <span>{s}</span>
                              {out ? <span className="size-picker__out">{t('pdp.sizeSoldOut')}</span> : null}
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>

          {available ? (
            <div className="stack" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
              <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="qty">
                  <button
                    type="button"
                    aria-label={t('a11y.qtyDecrease')}
                    disabled={qty <= 1}
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                  >
                    −
                  </button>
                  <span>{qty}</span>
                  <button
                    type="button"
                    aria-label={t('a11y.qtyIncrease')}
                    disabled={qty >= maxAddable}
                    onClick={() => setQty((q) => Math.min(maxAddable, q + 1))}
                  >
                    +
                  </button>
                </div>
                <button
                  type="button"
                  className="btn"
                  disabled={view.priceCents <= 0 || maxAddable < 1 || missingSize}
                  aria-describedby={missingSize ? 'pack-choose-size' : undefined}
                  onClick={() => {
                    addPack(view, qty, sizes);
                    openCart();
                  }}
                >
                  {t('pdp.addToSelection')}
                </button>
              </div>
              {missingSize ? (
                <p id="pack-choose-size" className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
                  {t('pack.chooseSizes')}
                </p>
              ) : maxAddable < 1 ? (
                <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('pdp.allInCart')}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
